import {
  Button,
  ConfirmationDialog,
  InputConfirmation,
  MessagePanel,
} from '@dittolive/anvil'
import React, { useCallback, useState } from 'react'

type Props = {
  /**
   * The name or ID to match for confirmation
   */
  matchTarget: string
  /**
   * Custom "database" text label (for renamed apps)
   */
  databaseText?: string
  /**
   * Whether the delete is currently in progress
   */
  isDeleting?: boolean
  /**
   * Error message to display
   */
  errorMessage?: string | null
  /**
   * Callback when delete is confirmed
   */
  onDelete: () => Promise<void>
  /**
   * Callback when modal is closed
   */
  onClose?: () => void
}

export default function DeleteDataBridgeModal({
  matchTarget,
  databaseText = 'data bridge',
  isDeleting = false,
  errorMessage: externalErrorMessage,
  onDelete,
  onClose,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMatch, setIsMatch] = useState(false)
  const [internalDeleting, setInternalDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleDelete = useCallback(async () => {
    if (!isMatch) {
      return
    }

    setInternalDeleting(true)
    setErrorMessage(null)
    try {
      await onDelete()
      setIsOpen(false)
      onClose?.()
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'An error occurred while deleting the data bridge'
      setErrorMessage(message)
    } finally {
      setInternalDeleting(false)
    }
  }, [onDelete, isMatch, onClose])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setIsMatch(false)
      setErrorMessage(null)
    }
  }

  const deletingState = isDeleting || internalDeleting
  const displayError = externalErrorMessage || errorMessage

  return (
    <div>
      <Button variant="critical" onClick={() => setIsOpen(true)}>
        Delete data bridge
      </Button>

      <ConfirmationDialog
        size="md"
        open={isOpen}
        onCancel={() => handleOpenChange(false)}
        onConfirm={handleDelete}
        title="Delete data bridge"
        confirmButtonText="Delete data bridge"
        cancelButtonDisabled={deletingState}
        confirmButtonDisabled={!isMatch || deletingState}
        variant="warning"
        description="Permanently delete your data bridge"
      >
        {displayError && (
          <MessagePanel
            variant="danger"
            message={displayError}
            className="mb-6"
          />
        )}

        <div className="flex flex-col space-y-4">
          <p className="text-foreground-normal text-base leading-5">
            Are you sure you want to delete your data bridge and all related
            configuration?
          </p>

          <MessagePanel
            variant="danger"
            message={`Once you confirm this action there's no going back. Be certain that you want to delete this ${databaseText} and all data related to it.`}
          />
        </div>

        <InputConfirmation onMatch={setIsMatch} value={matchTarget} />
      </ConfirmationDialog>
    </div>
  )
}
